import { z } from 'zod';
import {
  AppointmentMode,
  AppointmentPlatform,
  InteractionOrigin,
  InteractionResult,
  InteractionStatus,
  InteractionType,
  TASK_INTERACTION_TYPES,
} from '../enums';

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .nullable()
    .transform((v) => (v ? v : null));

/** Modalidad de la cita: presencial (lugar) o virtual (plataforma y enlace). Solo aplica a SE_CITO. */
export const appointmentDetailsSchema = z
  .object({
    mode: z.nativeEnum(AppointmentMode),
    platform: z.nativeEnum(AppointmentPlatform).optional().nullable(),
    /** Lugar de la cita presencial (sala de ventas, oficina, dirección). */
    location: optionalText(200),
    /** Enlace de la reunión virtual. */
    url: z
      .string()
      .trim()
      .max(500)
      .optional()
      .nullable()
      .transform((v) => (v ? v : null)),
    /** 7.22: asesor de sala que atenderá la cita (null = el mismo asesor del lead). */
    roomAdvisorId: z.string().uuid().optional().nullable(),
  })
  .superRefine((v, ctx) => {
    if (v.mode === 'VIRTUAL' && !v.platform)
      ctx.addIssue({ code: 'custom', path: ['platform'], message: 'Indica la plataforma' });
    if (v.url && !/^https?:\/\//i.test(v.url))
      ctx.addIssue({ code: 'custom', path: ['url'], message: 'El enlace debe empezar con http' });
  });
export type AppointmentDetails = z.infer<typeof appointmentDetailsSchema>;
export type AppointmentDetailsPayload = z.input<typeof appointmentDetailsSchema>;

/** Campos de la tarea programada como próximo paso (los comparten `nextStepSchema` y `reopenLeadSchema`). */
export const nextTaskFields = {
  /** Solo tipos de tarea: una nota no es un próximo paso (el seguimiento no la cuenta). */
  type: z.enum(TASK_INTERACTION_TYPES, {
    errorMap: () => ({
      message:
        'El siguiente paso debe ser una tarea (llamada, WhatsApp, cita, visita…), no una nota',
    }),
  }),
  scheduledAt: z.coerce.date(),
  description: optionalText(500),
  /** Solo si type = SE_CITO: modalidad, lugar / enlace y asesor de sala. */
  appointment: appointmentDetailsSchema.optional().nullable(),
};

/** Próximo paso obligatorio al completar una tarea: otra tarea o desistir con motivo. */
export const nextStepSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('TASK'), ...nextTaskFields }),
  z.object({
    kind: z.literal('DESIST'),
    reason: z.string().min(1, 'Selecciona un motivo').max(40),
    comment: optionalText(1000),
  }),
]);
export type NextStep = z.infer<typeof nextStepSchema>;

export const createInteractionSchema = z
  .object({
    type: z.nativeEnum(InteractionType),
    status: z
      .enum([InteractionStatus.PENDIENTE, InteractionStatus.COMPLETADA])
      .default(InteractionStatus.PENDIENTE),
    origin: z.nativeEnum(InteractionOrigin).optional(),
    scheduledAt: z.coerce.date().optional().nullable(),
    completedAt: z.coerce.date().optional().nullable(),
    visitAt: z.coerce.date().optional().nullable(),
    result: z.nativeEnum(InteractionResult).optional().nullable(),
    nonEffectiveReason: optionalText(40),
    description: optionalText(2000),
    unitId: z.string().uuid().optional().nullable(),
    /** Modalidad de la cita (solo SE_CITO pendiente). */
    appointment: appointmentDetailsSchema.optional().nullable(),
    /** Datos libres del canal (p. ej. conversación del bot, intención, sentimiento). */
    metadata: z.record(z.unknown()).optional(),
    /** Obligatorio si status = COMPLETADA y el lead sigue activo (regla de oro). */
    next: nextStepSchema.optional(),
  })
  .superRefine((v, ctx) => {
    if (
      v.status === InteractionStatus.PENDIENTE &&
      !v.scheduledAt &&
      v.type !== InteractionType.NOTA
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['scheduledAt'],
        message: 'Una tarea pendiente necesita fecha programada',
      });
    }
    if (v.type === InteractionType.SE_CITO && !v.scheduledAt) {
      ctx.addIssue({
        code: 'custom',
        path: ['scheduledAt'],
        message: 'Una cita requiere fecha y hora programada',
      });
    }
  });
export type CreateInteractionInput = z.infer<typeof createInteractionSchema>;
export type CreateInteractionPayload = z.input<typeof createInteractionSchema>;

export const completeInteractionSchema = z.object({
  completedAt: z.coerce.date().optional(),
  result: z.nativeEnum(InteractionResult).optional().nullable(),
  nonEffectiveReason: optionalText(40),
  description: optionalText(2000),
  /**
   * Fecha de la visita. `undefined` = no enviado (visita / proforma / cita toman la fecha de cierre);
   * `null` = el cliente no asistió / la visita no se realizó (no cuenta como visita ni como cita concretada).
   */
  visitAt: z.coerce.date().optional().nullable(),
  metadata: z.record(z.unknown()).optional(),
  next: nextStepSchema.optional(),
});
export type CompleteInteractionInput = z.infer<typeof completeInteractionSchema>;
export type CompleteInteractionPayload = z.input<typeof completeInteractionSchema>;

/** 7.22: confirmación de la cita concretada por el asesor digital y el de sala (`POST /interactions/:id/confirm`). */
export const confirmAppointmentSchema = z.object({
  /**
   * Casilla que se marca. Si no viene, la API deduce el rol de quien confirma: el asesor del lead como digital,
   * el asesor de sala como sala y quien gestiona marca las dos («Confirmar asistencia»).
   */
  role: z.enum(['DIGITAL', 'SALA']).optional(),
  /** false = retirar la confirmación (solo mientras la cita siga pendiente). */
  confirmed: z.boolean().default(true),
  description: optionalText(500),
  /** Obligatorio para quien completa la confirmación si el lead queda sin próximo paso. */
  next: nextStepSchema.optional(),
});
export type ConfirmAppointmentInput = z.infer<typeof confirmAppointmentSchema>;
export type ConfirmAppointmentPayload = z.input<typeof confirmAppointmentSchema>;

export const rescheduleInteractionSchema = z.object({
  scheduledAt: z.coerce.date(),
  description: optionalText(500),
  /** Cambiar la modalidad de una cita al reprogramarla (solo SE_CITO). */
  appointment: appointmentDetailsSchema.optional().nullable(),
});

/** Lote 2 · E1: anular una visita registrada por error (`POST /interactions/:id/annul-visit`); motivo obligatorio. */
export const annulVisitSchema = z.object({
  reason: z.string().trim().min(1, 'Indica el motivo').max(500),
});
export type AnnulVisitInput = z.infer<typeof annulVisitSchema>;

export const cancelInteractionSchema = z.object({
  reason: optionalText(500),
  next: nextStepSchema.optional(),
});

/** Tipos de interacción que exigen resultado al completarse. */
export const RESULT_REQUIRED_TYPES: readonly InteractionType[] = [
  InteractionType.LLAMADA,
  InteractionType.WHATSAPP,
  InteractionType.MAIL,
  InteractionType.SMS,
  InteractionType.REUNION,
];

export const INTERACTION_TYPE_LABELS: Record<InteractionType, string> = {
  LLAMADA: 'Llamada',
  WHATSAPP: 'WhatsApp',
  MAIL: 'Correo',
  SMS: 'SMS',
  REUNION: 'Reunión',
  VISITA: 'Visita',
  SE_CITO: 'Cita',
  PROFORMA: 'Proforma',
  NOTA: 'Nota',
};
